import { useDemoFetcherWithSwr } from "@/api";
import { debugModeAtom } from "@/atoms/debugModeAtom";
import { userAtom } from "@/atoms/userAtom";
import { AuthenticationError } from "@/common/AuthenticationError";
import { CountDisplay } from "@/common/CountDisplay";
import { InteractionListContent } from "@/common/InteractionListContent";
import { SortIndicator } from "@/common/SortIndicator";
import { Button } from "@/components/ui/button";
import { InteractionType } from "@/generatedApi/userUserInteractionLoggerApi";
import { isAuthenticated } from "@/utils/authErrors";
import { useAtomValue, useSetAtom } from "jotai";
import { Flag } from "lucide-react";
import { Helmet } from "react-helmet";
import { PageWrapper } from "../common/PageWrapper";
import { notFoundSpotIdsAtom } from "@/atoms/notFoundSpotIdsAtom";
import { useEffect } from "react";

export const VisitedPage = () => {
  const user = useAtomValue(userAtom);
  const debugMode = useAtomValue(debugModeAtom);
  const notFoundSpotIds = useAtomValue(notFoundSpotIdsAtom);
  const setNotFoundSpotIds = useSetAtom(notFoundSpotIdsAtom);

  // ユーザーが認証されていない場合はAPIコールをスキップ
  const shouldFetch = isAuthenticated(user);

  // 認証されている場合のみSWRを使用
  const swrResult = shouldFetch
    ? useDemoFetcherWithSwr({
        apiName: "user_interaction_logger",
        serviceName: "default",
        operationId: "getInteractionItemsByUserId",
        requestParameters: {
          userId: user!.userId,
          interactionType: InteractionType.VISITED,
          limit: 1000
        }
      })
    : null;

  // 認証状態に応じて結果を分岐
  const { data, isLoading, error, mutate } =
    shouldFetch && swrResult
      ? swrResult
      : {
          data: undefined,
          isLoading: false,
          error: shouldFetch ? null : new Error("Authentication required"),
          mutate: () => Promise.resolve(undefined)
        };

  // ページ表示時に404リストをクリア
  useEffect(() => {
    setNotFoundSpotIds(new Set<string>());
  }, [setNotFoundSpotIds]);

  const handleInteractionChange = () => {
    mutate();
  };

  // 404エラーを除外した実際のアイテム数
  const itemCount = (data?.items?.length || 0) - notFoundSpotIds.size;

  const headerCenter = !isLoading ? (
    <CountDisplay
      count={itemCount}
      unit="か所"
      icon={<Flag className="h-3 w-3" fill="currentColor" />}
    />
  ) : null;

  const headerRight = !isLoading ? <SortIndicator sortType="新着順" /> : null;

  return (
    <PageWrapper headerCenter={headerCenter} headerRight={headerRight}>
      <Helmet>
        <title>行ったスポット - Refloop</title>
      </Helmet>
      {debugMode && (
        <div className="flex items-center justify-between gap-2 rounded-md bg-gray-100 p-2">
          <span className="text-sm">{user ? `user id: ${user.userId}` : "Not logged in"}</span>
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const { signOut } = await import("aws-amplify/auth");
                await signOut();
                window.location.reload();
              }}
            >
              ログアウト
            </Button>
          )}
        </div>
      )}
      {!shouldFetch ? (
        <AuthenticationError
          title="ログインが必要です"
          message="行ったスポットの管理機能をご利用いただくには、アカウントにログインしてください。"
          icon={<Flag className="h-12 w-12" />}
        />
      ) : (
        <InteractionListContent
          data={data}
          isLoading={isLoading}
          error={error}
          emptyMessage="行ったスポットはありません。"
          enableSwipe={true}
          pageType="visited"
          onInteractionChange={handleInteractionChange}
          onRetry={mutate}
        />
      )}
    </PageWrapper>
  );
};
